import { type IRestResponse, useClientBuilder } from "@app-rest/index";

import type { IAttraction, IAttractionCartRequest, IAttractionCartResponse, IAttractionConfirmationEmail, IAttractionDepositsConfigurationResponse, IAttractionOfferDetails, IAttractionOffersRequest, IAttractionOffersResponse, IAttractionReservationDepositResponse, IAttractionReservationReferentRequest, IAttractionReservationTempResponse, IAttractionTimeSlotRequest, IAttractionTimeSlotResponse, IConfirmAttractionReservationRequest, ICreateAttractionReservationTempRequest, ICreateAttractionReservationTempResponse } from "./attractions.interfaces";

const useRest = useClientBuilder.createRestClient();
useRest().setOption("cacheInMemory", false);

export default function useAttractionsRestClient() {
	const { get, post, patch } = useRest();

	function getAttractionsList(systemId: number) {
		return get<IRestResponse<IAttraction>>(`/${systemId}/attractions-api/attractions`);
	}

	function attractionOffers(systemId: number, data: IAttractionOffersRequest) {
		return post<IAttractionOffersResponse>(
			`${systemId}/attractions-api/itemsAvailability/search`, {
				body: data,
			});
	}

	function attractionItemDetails(systemId: number, itemId: string) {
		return get<IAttractionOfferDetails>(`/${systemId}/attractions-api/attractions/catalog/${itemId}`, {
			throwExcluding: [{ statusCode: 404 }]
		});
	}

	function attractionTimeSlots(systemId: number, data: IAttractionTimeSlotRequest, abortController?: AbortController) {
		return post<IAttractionTimeSlotResponse>(
			`${systemId}/attractions-api/itemsAvailability/slots/search`, {
				body: data,
				abortController,
			});
	}

	function getAttractionReservationTemp(systemId: number, reservationId: string) {
		return get<IAttractionReservationTempResponse>(`/${systemId}/attractions-api/reservations/${reservationId}`, {
			throwExcluding: [{ statusCode: 404 }]
		});
	}

	function createAttractionReservationTemp(systemId: number, data: ICreateAttractionReservationTempRequest) {
		return post<ICreateAttractionReservationTempResponse>(
			`${systemId}/attractions-api/reservations `, {
				body: data,
				throwExcluding: [{ statusCode: 409 }]
			});
	}

	function attractionReservationReferent(systemId: number, reservationId: string, data: IAttractionReservationReferentRequest) {
		return patch(
			`/${systemId}/attractions-api/reservations/${reservationId}/referent`, {
				body: data,
				throwExcluding: [{ statusCode: 404 }]
			});
	}

	function confirmAttractionReservation(systemId: number, data: IConfirmAttractionReservationRequest) {
		return post(
			`/${systemId}/attractions-api/cart/confirm`, {
				body: data,
				throwExcluding: [{ statusCode: 404 }]
			});
	}

	function getAttractionConfirmationEmail(systemId: number) {
		return get<IAttractionConfirmationEmail>(`/${systemId}/attractions-api/email-setup/confirmation/custom-texts`);
	}

	function attractionCartSummary(systemId: number, data: IAttractionCartRequest) {
		return post<IAttractionCartResponse>(
			`/${systemId}/attractions-api/cart`, {
				body: data,
				throwExcluding: [{ statusCode: 404 }]
			});
	}

	function attractionDepositConfiguration(systemId: number) {
		return get<IAttractionDepositsConfigurationResponse>(`/${systemId}/attractions-api/deposits/configurations`);
	}

	function attractionReservationDeposit(systemId: number, reservationId: string) {
		return post<IAttractionReservationDepositResponse>(`/${systemId}/attractions-api/Reservations/${reservationId}/deposits`);
	}

	return {
		getAttractionsList,
		attractionOffers,
		attractionItemDetails,
		attractionTimeSlots,
		getAttractionReservationTemp,
		createAttractionReservationTemp,
		attractionReservationReferent,
		confirmAttractionReservation,
		getAttractionConfirmationEmail,
		attractionCartSummary,
		attractionDepositConfiguration,
		attractionReservationDeposit
	};
}
